import { QuestionContent, Question, isQuestionContent } from "@/types/test";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Code, Play } from "lucide-react";
import { useState, useEffect } from "react";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs";
import "prismjs/themes/prism.css";

interface CodingResponse {
  code: string;
  language: string;
}

interface CodingQuestionProps {
  question: Question;
  response: CodingResponse;
  onChange: (response: CodingResponse) => void;
}

const LANGUAGES = [
  { value: "javascript", label: "JavaScript" },
  { value: "python", label: "Python" },
  { value: "java", label: "Java" },
  { value: "cpp", label: "C++" },
];

// Cache for loaded language grammars
const loadedLanguages = new Map();

const loadLanguage = async (language: string) => {
  if (loadedLanguages.has(language)) {
    return loadedLanguages.get(language);
  }

  try {
    switch (language) {
      case "javascript":
        await import("prismjs/components/prism-javascript");
        loadedLanguages.set(language, languages.javascript);
        return languages.javascript;
      case "python":
        await import("prismjs/components/prism-python");
        loadedLanguages.set(language, languages.python);
        return languages.python;
      case "java":
        await import("prismjs/components/prism-java");
        loadedLanguages.set(language, languages.java);
        return languages.java;
      case "cpp":
        await import("prismjs/components/prism-cpp");
        loadedLanguages.set(language, languages.cpp);
        return languages.cpp;
      default:
        // Fallback to JavaScript for unknown languages
        await import("prismjs/components/prism-javascript");
        loadedLanguages.set(language, languages.javascript);
        return languages.javascript;
    }
  } catch (error) {
    console.warn(`Failed to load language ${language}:`, error);
    // Fallback to JavaScript
    loadedLanguages.set(language, languages.javascript);
    return languages.javascript;
  }
};

export function CodingQuestion({
  question,
  response,
  onChange,
}: CodingQuestionProps) {
  const [isTestCasesOpen, setIsTestCasesOpen] = useState(true);
  const [currentLanguageGrammar, setCurrentLanguageGrammar] = useState(languages.javascript);
  
  const questionContent = isQuestionContent(question.content) ? question.content : { question: '' };
  const codeTests = question.code_tests;
  
  // Initialize response if empty
  useEffect(() => {
    if (!response) {
      onChange({
        code: questionContent.starterCode || codeTests?.setup_code || "",
        language: codeTests?.language || questionContent.language || "javascript"
      });
    }
  }, [response, onChange, questionContent, codeTests]);

  // Load language grammar when language changes
  useEffect(() => {
    const currentLang = response?.language || "javascript";
    loadLanguage(currentLang).then(grammar => {
      setCurrentLanguageGrammar(grammar);
    });
  }, [response?.language]);

  const currentLanguage = LANGUAGES.find(lang => lang.value === response?.language) || LANGUAGES[0];

  const handleCodeChange = (code: string) => {
    onChange({
      ...response,
      code
    });
  };

  const handleLanguageChange = (language: string) => {
    onChange({
      ...response,
      language
    });
  };

  const highlightCode = (code: string) => {
    try {
      return highlight(code, currentLanguageGrammar, response?.language || "javascript");
    } catch (error) {
      return code;
    }
  };

  // Get visible test cases from the question data
  const visibleTestCases = codeTests?.test_cases?.filter(testCase => !testCase.hidden) || [];

  return (
    <div className="space-y-6">
      <h3 className="font-medium text-2xl text-foreground">{questionContent.question}</h3>
      
      {/* Test Cases Section */}
      {visibleTestCases.length > 0 && (
        <Collapsible open={isTestCasesOpen} onOpenChange={setIsTestCasesOpen}>
          <CollapsibleTrigger className="w-full">
            <Card className="hover:bg-accent/50 transition-colors cursor-pointer">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-base">
                  <div className="flex items-center gap-2">
                    <Play className="h-4 w-4" />
                    Test Cases ({visibleTestCases.length})
                  </div>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isTestCasesOpen ? 'rotate-180' : ''}`} />
                </CardTitle>
              </CardHeader>
            </Card>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-3">
              {visibleTestCases.map((testCase, index) => (
                <Card key={index} className="border-border">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="secondary">{testCase.name}</Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-muted-foreground mb-1">Input:</p>
                        <code className="block p-2 bg-muted rounded text-foreground font-mono">
                          {testCase.input || "No input"}
                        </code>
                      </div>
                      <div>
                        <p className="font-medium text-muted-foreground mb-1">Expected Output:</p>
                        <code className="block p-2 bg-muted rounded text-foreground font-mono">
                          {testCase.expected_output || "No expected output"}
                        </code>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Code Editor Section */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Code className="h-4 w-4" />
              Your Solution
            </CardTitle>
            <Select value={response?.language || codeTests?.language || "javascript"} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border border-border rounded-sm overflow-hidden">
            <Editor
              value={response?.code || ""}
              onValueChange={handleCodeChange}
              highlight={highlightCode}
              padding={16}
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                fontSize: 14,
                lineHeight: 1.5,
                minHeight: '300px',
                backgroundColor: 'hsl(var(--card))',
                color: 'hsl(var(--card-foreground))',
              }}
              className="min-h-[300px] bg-card text-card-foreground"
              placeholder="// Write your code here..."
            />
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Use Tab for indentation • Ctrl+A to select all
          </p>
        </CardContent>
      </Card>
    </div>
  );
}